import { reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { getMessageCountByMemberId } from '../../services/api/messaging/messages.ts'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
import { useAccessRightsStore } from '../accessRights/accessRights.ts'

// ====---------------====
// 💾 Types & Interfaces
// ====---------------====
type countMemberObject = {
	memberId: string
	count: number
}

type stateObject = {
	[key: string]: number
}

interface countResponse {
	success: boolean
	errorMessage: string
	data: Array<countMemberObject>
}

export const useMessageCountStore = defineStore(
	'messageCount',
	() => {
		// ====---------------====
		// 🏪 Stores
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()
		const rightsStore = ref<RightsObject | null>(null)

		// ====---------------====
		// 🗄️ State
		// ====---------------====
		const state = reactive<stateObject>({})
		const loaded = ref(false)

		// ====---------------====
		// 🏃‍♂️ Getters
		// ====---------------====
		async function getStore(): Promise<stateObject | null> {
			// Get AccessRights
			await useAccessRightsStore()
				.loadStore()
				.then((res: RightsObject | null) => (rightsStore.value = res))

			// Initialize Count store
			if (!loaded.value) await loadCountStore()

			return state
		}

		function getTotal(): number {
			let total: number = 0
			Object.values(state).forEach((member) => {
				total += member
			})

			return total
		}

		function getCountById(memberId: string): number | null {
			return state[memberId] ?? null
		}

		// ====---------------====
		// 🎬 Actions
		// ====---------------====
		function updateStore(memberId: string, count: number) {
			state[memberId] = count
		}

		function $reset() {
			Object.keys(state).forEach((key) => {
				delete state[key]
			})
			loaded.value = false
		}

		// ====---------------====
		// Loads the store with a promise
		// This keeps the store form loading multiple times
		// ====---------------====
		let loadCountRequest: Promise<string> | null = null
		async function loadCountStore() {
			// If promise is pending return promise
			if (loadCountRequest) {
				return loadCountRequest
			}

			// If no pending promise create one and fetch api

			loadCountRequest = new Promise(async (resolve, reject) => {
				if (!planStore.memberId) return false // TODO: comeback to this. The plan store should never have a null id
				try {
					await getMessageCountByMemberId(
						planStore.memberId,
						useAccessRightsStore().getAccessibleIdsArray()
					).then((res: countResponse) => {
						Object.values(res.data).forEach((x) => {
							state[x.memberId] = x.count
						})

						loaded.value = true
					})

					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					// clear promise
					loadCountRequest = null
				}
			})
			return loadCountRequest
		}

		// ====---------------====
		// 🚀 Exports
		// ====---------------====
		return {
			state,
			loaded,
			getStore,
			getTotal,
			getCountById,
			updateStore,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
